import {
  complement,
  emptyObject,
  emptyArray,
  constantFalse,
  identity,
  constant,
  getDeepestValues,
  add,
  concat,
} from '@packages/utilities/fp/utils';
import {deleteAt, moveElement} from '@packages/utilities/array';
import {
  matchExpression,
  EvalEnv,
  Component,
  Expressions,
  type ExpressionPathPart,
  type ExpressionPath,
  Plugins,
} from '@packages/systems/wfdl';
import {weakMemo} from '@packages/utilities/memo';
import {
  isUserComponentInst,
  findChild,
  findElement,
  filterElementChildren,
  contains,
  isInside,
  getChildren,
  getComponentInstRootElement,
  getPageComponentFromComponentMap,
  getClosestRichTextElement,
  getClosestFigureElement,
} from '@packages/utilities/platform';
import {updateElementData} from '@packages/utilities/platform-elementdata';
import {
  isDynamicBoundOrConditioned,
  isDynamoWrapper,
  isPagination,
  isDynamoEmptyElement,
  isDynamoItem,
  isDynamoList,
  isBody,
  isAnyCommerceDynListItemElement,
  isBlockExplicit,
  isBlockquote,
  isButton,
  isEmphasized,
  isHeading,
  isLink,
  isParagraph,
  isRichText,
  isSpan,
  isStrong,
  isSubscript,
  isSuperscript,
  isTypographicBlock,
  isTypographic,
  isCommerceCheckoutSummaryTextSpacingOnDiv,
  getLinkElementMode,
} from '@packages/utilities/elementTypeUtils';
import {Result, Err, Ok} from '@packages/utilities/fp/result';
import type {CollectionRecordType as Collection} from '@packages/systems/dynamo/records/CollectionRecord';

import {
  RESULTS_LENGTH_DEFAULT,
  SNIPPET_LENGTH_DEFAULT,
} from '@packages/systems/core/constants/SiteSearch';

import {
  CONDITION_INVISIBLE_CLASS,
  COLLECTION_LIST_QUERY_MODES,
  MIN_COLLECTION_LIST_OFFSET,
  CollectionListQueryMode,
} from '@packages/systems/dynamo/constants';

import type {DeprecatedConditionsT} from '@packages/systems/dynamo/types';
import type {BindingPathMapPojo} from '@packages/systems/dynamo/binding-types';
import {isShallowBindingPathPojo} from '@packages/systems/dynamo/binding-types';
import {List, Map as ImmutableMap} from 'immutable';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import type {ItemsType} from '@packages/systems/core/records/CollectionStateType';
import type {
  BindingContextKeyType,
  DynamoBindingProps,
} from '@packages/systems/dynamo/binding-props';
import {BINDING_CONTEXT_KEYS} from '@packages/systems/core/records/Binding';
import {matchesDynContextConstant} from '@packages/systems/dynamo/utils/DynamoItemUtils';
import {pluralize} from '@packages/utilities/string-utils';

import {
  EBoolean,
  EElement,
  EList,
  ENumber,
  ERecord,
  EText,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {capDynamoWrapperLimit} from '@packages/systems/dynamo/utils/PaginationUtils';
import {
  getDynamoBindingPaths,
  isDynamoVariable,
} from '@packages/systems/dynamo/wfdl';
import type {
  GeneralFieldType,
  ListConnectionDef,
} from '@packages/systems/dynamo/source-types';
import {getConfigOutsideOfReact} from '@packages/systems/feature-config/client';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {Pair, getFirst, getSecond, pair} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {
  defaultButtonChildren,
  defaultTextLinkChildren,
  defaultHeadingChildren,
  defaultParagraphChildren,
  defaultStrongChildren,
  defaultEmChildren,
  defaultSupChildren,
  defaultSubChildren,
  defaultSpanChildren,
  defaultTextDivChildren,
  defaultBlockquoteChildren,
  defaultRichTextChildren,
} from '@packages/systems/core/constants/DomNodeDefaults';
import {
  OperatorId,
  mapFieldsQuery,
} from '@packages/systems/dynamo/utils/DynQueryUtils';
import {SortData} from '@packages/systems/dynamo/utils/CollectionUtils';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';

const dynPath: ExpressionPathPart = {in: 'Record', at: 'dyn'};
const searchPath: ExpressionPathPart = {in: 'Record', at: 'search'};
const paginationPath: ExpressionPathPart = {in: 'Record', at: 'pagination'};
const collectionPath: ExpressionPathPart = {in: 'Record', at: 'collection'};
const bindPath: ExpressionPathPart = {in: 'Record', at: 'bind'};
const hrefPath: ExpressionPathPart = {in: 'Record', at: 'href'};
const innerHTMLPath: ExpressionPathPart = {in: 'Record', at: 'innerHTML'};
const dataWHrefPath: ExpressionPathPart = {in: 'Record', at: 'dataWHref'};
const srcPath: ExpressionPathPart = {in: 'Record', at: 'src'};
const altPath: ExpressionPathPart = {in: 'Record', at: 'alt'};
const mediaPath: ExpressionPathPart = {in: 'Record', at: 'media'};
const limitPath: ExpressionPathPart = {in: 'Record', at: 'limit'};
const queryPath: ExpressionPathPart = {in: 'Record', at: 'query'};
const skuIdPath: ExpressionPathPart = {in: 'Record', at: 'skuId'};
const productIdPath: ExpressionPathPart = {in: 'Record', at: 'productId'};
const optionSetsPath: ExpressionPathPart = {in: 'Record', at: 'optionSets'};
const optionValuesPath: ExpressionPathPart = {in: 'Record', at: 'optionValues'};
const dataPath: ExpressionPathPart = {in: 'Record', at: 'data'};
const tokenPath: ExpressionPathPart = {in: 'Record', at: 'token'};
const inventoryPath: ExpressionPathPart = {in: 'Record', at: 'inventory'};
const repeaterPath: ExpressionPathPart = {in: 'Record', at: 'repeater'};
const stylePath: ExpressionPathPart = {in: 'Record', at: 'style'};
const filterPath: ExpressionPathPart = {in: 'Record', at: 'filter'};
const fieldsPath: ExpressionPathPart = {in: 'Record', at: 'fields'};

const collectionIdPath: ExpressionPath = [
  dynPath,
  collectionPath,
  {in: 'Record', at: 'id'},
];

export type NodeBindingType =
  | 'Collection List'
  | 'Multi-reference Field'
  | 'Multi-image'
  | 'Text'
  | 'Rich Text'
  | 'Video'
  | 'Text Reference Field'
  | 'Rich Text Reference Field'
  | 'Video Reference Field'
  | 'Text Reference Item'
  | 'Rich Text Reference Item'
  | 'Video Reference Item'
  | 'Image'
  | 'Image Reference Field'
  | 'Image Reference Item'
  | 'Image Alt Text'
  | 'Image Alt Text Reference Field'
  | 'Image Alt Text Reference Item'
  | 'Link'
  | 'Link Reference Field'
  | 'Link Reference Item'
  | 'Template Page Link'
  | 'Template Page Link Reference Field'
  | 'Template Page Link Reference Item'
  | 'Code Embed'
  | 'Conditional with reference'
  | 'Conditional Visibility'
  | 'Conditional Visibility Reference Field'
  | 'Conditional Visibility Reference Item'
  | 'Conditional Visibility With Reference'
  | 'Color'
  | 'Color Reference Field'
  | 'Color Reference Item'
  | 'Background Image'
  | 'Background Image Reference Field'
  | 'Background Image Reference Item'
  | 'Background Color'
  | 'Background Color Reference Field'
  | 'Background Color Reference Item'
  | 'Border Color'
  | 'Border Color Reference Field'
  | 'Border Color Reference Item'
  | 'Filter'
  | 'Filter Reference Item'
  | 'Filter with Reference Item'
  | 'Sort Order'
  | 'Element Setting Override'
  | 'Symbol Override'
  | 'Filter Component Property Field'
  | 'Sort Component Property Field'
  | 'Filter Component Property'
  | 'Sort Component Property';

export type ContextCollections = {
  listCollection: Collection | null | undefined;
  pageCollection: Collection | null | undefined;
  contextCollection: Collection | null | undefined;
};

export type DynListLimitsConfig = {
  MAX_NESTED_DYNLISTS_PER_PAGE: number;
  MAX_DYNLISTS_PER_PAGE: number;
  MAX_NESTED_COLLECTION_LIST_LIMIT: number;
  MAX_COLLECTION_LIST_LIMIT: number;
};

/**
 * As of May 15th, the rollout has the following values listed, so matching our defaults to that
 * {
 *   "MAX_NESTED_DYNLISTS_PER_PAGE": 2,
 *   "MAX_DYNLISTS_PER_PAGE": 20,
 *   "MAX_NESTED_COLLECTION_LIST_LIMIT": 10,
 *   "MAX_COLLECTION_LIST_LIMIT": 100,
 * }
 */
export const defaultDynListLimits: DynListLimitsConfig = {
  MAX_NESTED_DYNLISTS_PER_PAGE: 2,
  MAX_DYNLISTS_PER_PAGE: 20,
  MAX_NESTED_COLLECTION_LIST_LIMIT: 10,
  MAX_COLLECTION_LIST_LIMIT: 100,
};

export function getCollectionIdFromElement(
  element: ExpressionElement
): string | undefined {
  return Expressions.getElementDataAt(
    element,
    collectionIdPath,
    'Text',
    undefined
  );
}

const collectionItemRefSetPath: ExpressionPath = [
  dynPath,
  collectionPath,
  {in: 'Record', at: 'itemRefSet'},
];

export function getCollectionItemRefSetSlugFromElement(
  element: ExpressionElement
): string | undefined {
  return Expressions.getElementDataAt(
    element,
    collectionItemRefSetPath,
    'Text',
    undefined
  );
}

const contextCollectionIdPath: ExpressionPath = [
  dynPath,
  collectionPath,
  {in: 'Record', at: 'contextCollectionId'},
];

export function getContextCollectionIdFromElement(
  element: ExpressionElement
): string | undefined {
  return Expressions.getElementDataAt(
    element,
    contextCollectionIdPath,
    'Text',
    undefined
  );
}

/**
 *
 * Retrieves the "query" record of the Expression
 */
export const getQuery = (element: ExpressionElement): Expression | null => {
  const dynListQuery = Expressions.getIn(Expressions.getElementData(element), [
    dynPath,
    queryPath,
  ]);

  return dynListQuery || null;
};

export const limitItemsPath = [dynPath, queryPath, limitPath];

export function getDynListQueryLimitExprFromElement(
  element: ExpressionElement
): Expression | null {
  const dynListQueryLimitExpr = Expressions.getIn(
    Expressions.getElementData(element),
    limitItemsPath
  );

  return dynListQueryLimitExpr || null;
}

export const getQueryLimit = (element: ExpressionElement): number | null => {
  const numberVal = Expressions.getElementDataAt(
    element,
    limitItemsPath,
    'Number',
    null
  );

  if (numberVal !== null) {
    return numberVal;
  }

  const textVal = Expressions.getElementDataAt(
    element,
    limitItemsPath,
    'Text',
    null
  );

  if (!textVal) {
    return null;
  }

  const parsedNumberVal = Number(textVal);

  if (!isNaN(parsedNumberVal)) {
    return parsedNumberVal;
  }

  return null;
};

export function isPaginatedDynamoWrapperElement(
  element: ExpressionElement
): boolean {
  return Boolean(findChild(element, isPagination));
}

const repeaterIdPath = [dynPath, bindPath, repeaterPath];

const isSetTextOrEnum = matchExpression({
  Text: (val) => val === 'Set',
  Enum: (val) => val === 'Set',
  _: constantFalse,
});
const isSetFieldInList = matchExpression({
  List: (val) =>
    val.some((expr) => {
      const type = Expressions.getIn(expr, [{in: 'Record', at: 'type'}]);
      return type && isSetTextOrEnum(type);
    }),
  _: constantFalse,
});
export function isSetFieldDynamoWrapperElement(element: ExpressionElement) {
  const repeaterList = Expressions.getIn(
    Expressions.getElementData(element),
    repeaterIdPath
  );
  return (
    isDynamoWrapper(element) &&
    !getCollectionIdFromElement(element) &&
    repeaterList != null &&
    isSetFieldInList(repeaterList)
  );
}

export function isConnectedDynamoWrapper(element: ExpressionElement): boolean {
  return Boolean(
    isSetFieldDynamoWrapperElement(element) ||
    getCollectionIdFromElement(element)
  );
}
/**
 * Determines whether a dynamic list is bound to an ItemRefSet collection of an item.
 */
export function isItemRefSetBoundList(element: ExpressionElement): boolean {
  return (
    getCollectionItemRefSetSlugFromElement(element) !== undefined &&
    getContextCollectionIdFromElement(element) !== undefined
  );
}

/* Check if a DynamoWrapper element is connected to a multi-reference field */
export function isMultiReferenceFieldConnectedDynamoWrapperElement(
  element: ExpressionElement
): boolean {
  return (
    getCollectionIdFromElement(element) !== undefined &&
    isItemRefSetBoundList(element)
  );
}

/* Check if a DynamoWrapper element is connected directly to a collection */
export function isCollectionConnectedDynamoWrapperElement(
  element: ExpressionElement
): boolean {
  return (
    getCollectionIdFromElement(element) !== undefined &&
    getCollectionItemRefSetSlugFromElement(element) === undefined &&
    getContextCollectionIdFromElement(element) === undefined
  );
}

/*
  Check if DynamoWrapper pagination settings can be updated

  DynamoWrapper pagination limit can be changed or pagination can be disabled
  only if it is connected directly to a collection and it is already
  paginated.
*/
export function canUpdateDynamoWrapperPagination(
  element: ExpressionElement
): boolean {
  return (
    isCollectionConnectedDynamoWrapperElement(element) &&
    isPaginatedDynamoWrapperElement(element)
  );
}

export const PAGINATION_MAX_LIMIT: number = 100;
export const paginationItemsPath = [paginationPath, limitPath];

export function getPaginationLimit(element: ExpressionElement): number {
  return Expressions.getElementDataAt(
    element,
    paginationItemsPath,
    'Number',
    PAGINATION_MAX_LIMIT
  );
}

export function setPaginationLimit(
  element: ExpressionElement,
  value: number
): ExpressionElement {
  const dataExpr = Expressions.getElementData(element);
  const nextDataExpr = Expressions.sparseSetIn(
    dataExpr,
    paginationItemsPath,
    ENumber(value)
  );
  return Expressions.setElementData(element, nextDataExpr);
}

export function removePagination(element: ExpressionElement) {
  const elementData = Expressions.getElementData(element);
  const nextDataExpr = Expressions.deleteInRecord(elementData, [
    paginationPath,
  ]);

  return Expressions.setElementData(element, nextDataExpr);
}

export function disableDynamoWrapperPagination(
  element: ExpressionElement
): ExpressionElement {
  return filterElementChildren(element, complement(isPagination));
}

export function getDynamoItemElement(element: ExpressionElement) {
  if (!isDynamoWrapper(element)) {
    return undefined;
  }
  const dynamoList = findChild(element, isDynamoList);
  return dynamoList ? findChild(dynamoList, isDynamoItem) : undefined;
}

export function getDynamoEmptyElement(element: ExpressionElement) {
  if (!isDynamoWrapper(element)) {
    return undefined;
  }
  return findChild(element, isDynamoEmptyElement);
}

export function closestDynamoWrapperElement(
  tree: Expression,
  element: ExpressionElement
) {
  return Expressions.closestElement(tree, element.id, isDynamoWrapper);
}

export const changeDynListUiState =
  (uiState: 'items' | 'empty') => (data: Expression) =>
    Expressions.sparseSetIn(data, TEMP_STATE_PATH, EText(uiState));

export const removeDynListUiState = (data: Expression) =>
  Expressions.recursiveDeleteInRecord(data, TEMP_STATE_PATH);

export function closestDynamoItemElement(
  tree: Expression,
  element: ExpressionElement
) {
  return Expressions.closestElement(tree, element.id, isDynamoItem);
}

export const isInsideDynamoItem = (
  element: ExpressionElement,
  tree: Expression
) => Boolean(closestDynamoItemElement(tree, element));

export const isInsideRichTextFigure = (
  tree: Expression,
  element: ExpressionElement
) =>
  Boolean(
    getClosestRichTextElement(tree, element.id) &&
    getClosestFigureElement(tree, element.id)
  );

export const isInsideAnyCommerceDynListItem = (
  element: ExpressionElement,
  tree: Expression
) => {
  return Boolean(closestAnyCommerceDynListItem(element, tree));
};

export const closestAnyCommerceDynListItem = (
  element: ExpressionElement,
  tree: Expression
) =>
  Expressions.closestElement(tree, element.id, isAnyCommerceDynListItemElement);

const BIND_PATHS = {
  dyn: {
    root: [dynPath, bindPath],
    innerHTML: [dynPath, bindPath, innerHTMLPath],
    href: [dynPath, bindPath, hrefPath],
    dataWHref: [dynPath, bindPath, dataWHrefPath],
    src: [dynPath, bindPath, srcPath],
    alt: [dynPath, bindPath, altPath],
    media: [dynPath, bindPath, mediaPath],
    skuId: [dynPath, bindPath, skuIdPath],
    productId: [dynPath, bindPath, productIdPath],
    optionSets: [dynPath, bindPath, optionSetsPath],
    optionValues: [dynPath, bindPath, optionValuesPath],
    data: [dynPath, bindPath, dataPath],
    token: [dynPath, bindPath, tokenPath],
    inventory: [dynPath, bindPath, inventoryPath],
    repeater: [dynPath, bindPath, repeaterPath],
    style: [dynPath, bindPath, stylePath],
  },
  search: {
    root: [searchPath, bindPath],
    innerHTML: [searchPath, bindPath, innerHTMLPath],
    href: [searchPath, bindPath, hrefPath],
    dataWHref: [searchPath, bindPath, dataWHrefPath],
    src: [searchPath, bindPath, srcPath],
    alt: [searchPath, bindPath, altPath],
    media: [searchPath, bindPath, mediaPath],
    skuId: [searchPath, bindPath, skuIdPath],
    productId: [searchPath, bindPath, productIdPath],
    optionSets: [searchPath, bindPath, optionSetsPath],
    optionValues: [searchPath, bindPath, optionValuesPath],
    data: [searchPath, bindPath, dataPath],
    token: [searchPath, bindPath, tokenPath],
    inventory: [searchPath, bindPath, inventoryPath],
    repeater: [searchPath, bindPath, repeaterPath],
    style: [searchPath, bindPath, stylePath],
  },
};

type BindingProperty = keyof DynamoBindingProps;

/**
 * Detects whether an element is bound to a dynamic path.
 */
export function isDynBoundElement(
  element: ExpressionElement,
  property: BindingProperty | null = null
): boolean {
  return _elementHasBindingWithSource(element, 'dyn', property);
}

const getExpressionVal = matchExpression({
  Record: identity,
  List: identity,
  _: constant({}),
});
function _elementHasBindingWithSource(
  element: ExpressionElement,
  bindingSource: BindingContextKeyType,
  property: BindingProperty | null = null
): boolean {
  const path = property
    ? BIND_PATHS[bindingSource][property]
    : BIND_PATHS[bindingSource].root;

  if (!path) return false;

  const bindings = Expressions.getIn(Expressions.getElementData(element), path);
  const val = !bindings ? emptyObject : getExpressionVal(bindings);

  return val
    ? Array.isArray(val)
      ? val.length > 0
      : Object.keys(val).length > 0
    : false;
}

export function isBoundElement(
  element: ExpressionElement,
  property?: BindingProperty | null
): boolean {
  return (
    _elementHasBindingWithSource(element, BINDING_CONTEXT_KEYS.dyn, property) ||
    _elementHasBindingWithSource(element, BINDING_CONTEXT_KEYS.search, property)
  );
}

const dynConditionClassFalseInvisiblePath: ExpressionPath = [
  dynPath,
  {in: 'Record', at: 'condition'},
  {in: 'Record', at: 'class'},
  {in: 'Record', at: 'false'},
  {in: 'Record', at: CONDITION_INVISIBLE_CLASS},
];

/**
 * Expression path for conditional visibility settings fields
 * Used to identify and work with conditional visibility field records
 */
export const condVisSettingsPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'condition'},
  {in: 'Record', at: 'class'},
  {in: 'Record', at: 'false'},
  {in: 'Record', at: CONDITION_INVISIBLE_CLASS},
  {in: 'Record', at: 'fields'},
] satisfies ExpressionPath;

export function getConditionDataExprFromElement(
  element: ExpressionElement
): Expression | null {
  const conditionDataExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynConditionClassFalseInvisiblePath
  );

  return conditionDataExpr || null;
}

const dynConditionClassFalseInvisibleFieldsPath: ExpressionPath = [
  ...dynConditionClassFalseInvisiblePath,
  {in: 'Record', at: 'fields'},
];

export function getConditionFieldsExpr(
  element: ExpressionElement
): Expression | null {
  const conditionDataExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynConditionClassFalseInvisibleFieldsPath
  );

  return conditionDataExpr || null;
}

type ConditionData = {
  fields: DeprecatedConditionsT;
};

export function getConditionDataFromElement(
  element: ExpressionElement
): ConditionData | null {
  const conditionDataExpr = getConditionDataExprFromElement(element);

  if (!conditionDataExpr) {
    return null;
  }

  // TODO: should this "any" be in expression format?
  return Expressions.evaluate(EvalEnv.emptyEvalEnv, conditionDataExpr);
}

export function getConditionFieldsFromElement(
  element: ExpressionElement
): DeprecatedConditionsT {
  // TODO: should this "any" be in expression format?
  return getConditionDataFromElement(element)?.fields ?? emptyObject;
}

const dynBindPath = [dynPath, bindPath];

/**
 * Returns the dynamic binding path map (`data.dyn.bind`) for an element, or an
 * empty object when the element has no dynamic bindings.
 */
export function getDynBindingsFromElement(
  element: ExpressionElement
): BindingPathMapPojo {
  const dynBindData = Expressions.getIn(
    Expressions.getElementData(element),
    dynBindPath
  );

  if (!dynBindData) {
    return emptyObject;
  }

  // TODO: should these be in expression format?
  return Expressions.evaluate(
    EvalEnv.emptyEvalEnv,
    dynBindData
  ) as BindingPathMapPojo;
}

// The Immutable collections + items context binding resolvers read against.
interface DynamoContextMap<T> extends ImmutableMap<string, any> {
  get<K extends keyof T>(name: K): T[K];
}

export type DynamoItemContext = DynamoContextMap<{
  collections: ImmutableMap<string, any>;
  items: ItemsType;
}>;

// A binding part that hops through an item reference field rather than
// resolving a value directly.
function isItemRefType(binding: ImmutableMap<string, any>): boolean {
  const type = ImmutableMap.isMap(binding)
    ? binding.get('type')
    : (binding as {type?: string}).type;
  return type === 'ItemRef' || type === 'ItemRefSet';
}

// Whether any part of a binding list is bound to the given field type.
function hasBindingType(
  bindingList: List<ImmutableMap<string, any>> | null | undefined,
  type: string
): boolean {
  return Boolean(
    bindingList && bindingList.find((binding) => binding.get('type') === type)
  );
}

// The slug of the first non-`ItemRef` part — i.e. the field actually read.
function getBoundFieldSlug(
  bindingsList: List<ImmutableMap<string, any>>
): string | null | undefined {
  const bindingField = bindingsList.find((binding) => !isItemRefType(binding));
  return bindingField && bindingField.get('slug');
}

// Follows any `ItemRef` hops in the binding list to resolve the collection/item
// the bound field actually lives on. Falls back to the passed base identity when
// there is no ref hop.
function getItemIdentity({
  collectionId,
  itemId,
  dynamoContext,
  bindingsList,
}: {
  collectionId: string;
  itemId?: string;
  dynamoContext: DynamoItemContext;
  bindingsList: List<ImmutableMap<string, any>>;
}): {collectionId?: string; itemId?: string} {
  const collections = dynamoContext.get('collections');
  const items = dynamoContext.get('items');
  if (!collections || !items) {
    return emptyObject;
  }

  const baseCollection = collections.get(collectionId);
  const baseItem: ItemRecordType | null = itemId ? items.get(itemId) : null;

  if (!baseCollection) {
    return emptyObject;
  }

  const expandedIdentity = bindingsList.reduce((result, binding) => {
    if (isItemRefType(binding)) {
      const boundSlug = binding.get('slug');
      const slugField = baseCollection
        .get('fields', List())
        // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
        .find((field) => field.get('slug') === boundSlug);
      const refCollectionId: string | null = slugField
        ? slugField.getIn(['validations', 'collectionId'])
        : null;
      const refItemId = baseItem ? baseItem.get(boundSlug) : null;
      const expandedCollection = refCollectionId
        ? collections.get(refCollectionId)
        : null;
      const expandedItem = refItemId ? items.get(refItemId) : null;

      if (expandedCollection) {
        return {
          collectionId: expandedCollection.get('_id'),
          itemId: expandedItem ? expandedItem.get('_id') : undefined,
        };
      }
    }
    return result;
  }, emptyObject);

  if (expandedIdentity !== emptyObject) {
    return expandedIdentity;
  }

  return {collectionId, itemId};
}

/**
 * Reads the parts of a single dynamic binding (e.g. `data.dyn.bind.href`) off an
 * element as an Immutable `List<Map>` — the shape the binding-list resolvers
 * (`getBoundFieldSlug`, `getItemIdentity`, `hasBindingType`) expect. Returns an
 * empty `List` when the key is unbound.
 */
function getBindingListForKey(
  element: ExpressionElement,
  bindingKey: string
): List<ImmutableMap<string, any>> {
  const binding = getDynBindingsFromElement(element)[bindingKey];
  if (!binding) {
    return List();
  }
  // `href` bindings are shallow (an array of `{id, slug, type}` parts); flatten
  // the deep shape defensively.
  const parts = isShallowBindingPathPojo(binding)
    ? binding
    : Object.values(binding).flat();
  return List(parts.map((part) => ImmutableMap(part)));
}

/**
 * Returns the first field type in `acceptedTypes` that `bindingKey` is bound to,
 * or `null` if unbound. The field type is persisted inline on each binding part
 * (`data.dyn.bind.<key>` → `{id, slug, type}`), so no collection schema is
 * required. Types are checked in `acceptedTypes` order.
 */
export function getBoundFieldTypeForKey<T extends GeneralFieldType>(
  element: ExpressionElement,
  bindingKey: string,
  acceptedTypes: readonly T[]
): T | null {
  const bindingList = getBindingListForKey(element, bindingKey);
  if (bindingList.isEmpty()) {
    return null;
  }
  return (
    acceptedTypes.find((type) => hasBindingType(bindingList, type)) ?? null
  );
}

/**
 * Resolves the runtime value of a bound field (e.g. `href`) for the given
 * editing context. Returns `null` when the item can't be resolved and
 * `undefined` when the binding has no field slug.
 */
export function getBoundFieldValueForElement({
  element,
  bindingKey,
  dynamoContext,
  collectionId,
  itemId,
}: {
  element: ExpressionElement;
  bindingKey: string;
  dynamoContext: DynamoItemContext;
  collectionId: string;
  itemId: string;
}): string | null | undefined {
  const bindingsList = getBindingListForKey(element, bindingKey);
  const slug = getBoundFieldSlug(bindingsList);
  const {itemId: properItemId} = getItemIdentity({
    collectionId,
    itemId,
    dynamoContext,
    bindingsList,
  });

  const items = dynamoContext.get('items');
  const originalItem = properItemId ? items.get(properItemId) : null;

  if (!originalItem) {
    return null;
  }

  return slug ? originalItem.get(slug) : undefined;
}

const searchResultsLengthPath: ExpressionPath = [
  searchPath,
  {in: 'Record', at: 'resultsLength'},
];

/**
 * Expression element equivalent that replaced this abstract node version:
 *
 * ```js
 * const getResultsLength = getElse(
 *   ['data', 'search', 'resultsLength'],
 *   RESULTS_LENGTH_DEFAULT
 * );
 * ```
 */
export function getSearchResultsLengthFromElement(
  element: ExpressionElement
): number {
  const searchResultsLength = Expressions.getElementDataAt(
    element,
    searchResultsLengthPath,
    'Number',
    RESULTS_LENGTH_DEFAULT
  );
  return searchResultsLength;
}

const searchBindPath = [searchPath, bindPath];

/**
 * Gets the binding data for a search element
 */
export function getSearchBindingsFromElement(
  element: ExpressionElement
): BindingPathMapPojo {
  const searchBindData = Expressions.getIn(
    Expressions.getElementData(element),
    searchBindPath
  );

  if (!searchBindData) {
    return emptyObject;
  }

  // TODO: should these be in expression format?
  return Expressions.evaluate(EvalEnv.emptyEvalEnv, searchBindData) as any;
}

const searchSnippetLengthPath: ExpressionPath = [
  searchPath,
  {in: 'Record', at: 'snippetLength'},
];

/**
 * Expression element equivalent that replaced this abstract node version:
 *
 * ```js
 * const getSnippetLength = getElse(
 *   ['data', 'search', 'snippetLength'],
 *   SNIPPET_LENGTH_DEFAULT
 * );
 * ```
 */
export function getSearchSnippetLengthFromElement(
  element: ExpressionElement
): number {
  const snippetLength = Expressions.getElementDataAt(
    element,
    searchSnippetLengthPath,
    'Number',
    SNIPPET_LENGTH_DEFAULT
  );
  return snippetLength;
}

const tempSearchPreviewQueryPath: ExpressionPath = [
  {in: 'Record', at: 'temp'},
  {in: 'Record', at: 'searchPreviewQuery'},
];

/**
 * Expression element equivalent that replaced this abstract node version:
 *
 * ```js
 * const getSearchPreviewQuery = getElse(
 *   ['data', 'temp', 'searchPreviewQuery'],
 *   ''
 * );
 * ```
 */
export function getSearchPreviewQueryFromElement(
  element: ExpressionElement
): string {
  const previewQuery = Expressions.getElementDataAt(
    element,
    tempSearchPreviewQueryPath,
    'Text',
    ''
  );
  return previewQuery.trim();
}

export const dynQueryFieldsPath: ExpressionPath = [
  dynPath,
  queryPath,
  filterPath,
  fieldsPath,
];

export const dynQueryFilterPath: ExpressionPath = [
  dynPath,
  queryPath,
  filterPath,
];

export function getDynListQueryFieldsExprFromElement(
  element: ExpressionElement
): Expression | null {
  const dynListQueryFields = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryFieldsPath
  );

  return dynListQueryFields || null;
}

export function getDynListQueryFilterExprFromElement(
  element: ExpressionElement
): Expression | null {
  const dynListQueryFilter = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryFilterPath
  );

  return dynListQueryFilter || null;
}

export const dynQueryCuratedPath: ExpressionPath = [
  dynPath,
  queryPath,
  {in: 'Record', at: 'curated'},
];

export function getDynListCuratedIdsFromElement(
  element: ExpressionElement
): string[] | null {
  const curatedExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryCuratedPath
  );

  if (!curatedExpr) {
    return null;
  }
  const curatedRecord = Expressions.getRecord(curatedExpr);
  const curatedIds = curatedRecord?.itemIds
    ? Expressions.getList(curatedRecord.itemIds)
        ?.map((expr) => Expressions.getText(expr))
        .filter(Boolean)
    : null;
  // @ts-expect-error - TS2322 - Type '(string | null)[]' is not assignable to type 'string[]'.
  return curatedIds ? curatedIds : emptyArray;
}

const dynQueryModePath: ExpressionPath = [
  dynPath,
  queryPath,
  {in: 'Record', at: 'mode'},
];

export function getDynListQueryModeFromElement(
  element: ExpressionElement
): CollectionListQueryMode {
  const mode = Expressions.getElementDataAt(
    element,
    dynQueryModePath,
    'Text',
    COLLECTION_LIST_QUERY_MODES.DYNAMIC
  );

  if (mode === String(COLLECTION_LIST_QUERY_MODES.CURATED)) {
    return COLLECTION_LIST_QUERY_MODES.CURATED;
  }

  // Default to dynamic when not explicitly in curated mode.
  return COLLECTION_LIST_QUERY_MODES.DYNAMIC;
}

export const dynQueryOffsetPath: ExpressionPath = [
  dynPath,
  queryPath,
  {in: 'Record', at: 'offset'},
];

export function getDynListQueryOffsetExprFromElement(
  element: ExpressionElement
): Expression | null {
  const dynListQueryOffsetExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryOffsetPath
  );

  return dynListQueryOffsetExpr || null;
}

/**
 * Returns the dynamic list query offset for an element, or null when none is set.
 */
export function getQueryOffset(element: ExpressionElement): number | null {
  const numberVal = Expressions.getElementDataAt(
    element,
    dynQueryOffsetPath,
    'Number',
    null
  );

  if (numberVal !== null) {
    return numberVal;
  }

  const textVal = Expressions.getElementDataAt(
    element,
    dynQueryOffsetPath,
    'Text',
    null
  );

  if (!textVal) {
    return null;
  }

  const parsedNumberVal = Number(textVal);

  if (!isNaN(parsedNumberVal)) {
    return parsedNumberVal;
  }

  return null;
}

export const dynQueryFilterForPath: ExpressionPath = [
  dynPath,
  queryPath,
  filterPath,
  {in: 'Record', at: 'filterFor'},
];

export const dynQuerySortPath: ExpressionPath = [
  dynPath,
  queryPath,
  {in: 'Record', at: 'sort'},
];

function nonNullish<V>(v: V): v is NonNullable<V> {
  return v !== undefined && v !== null;
}

function mapRecordToSortData(
  record: Record<string, Expression>
): SortData | null {
  const {field: fieldExpr, direction: directionExpr} = record;
  if (!fieldExpr || !directionExpr) return null;

  const field = Expressions.getText(fieldExpr);
  const direction = Expressions.getText(directionExpr);
  if (!field || !direction) return null;

  return {field, direction} as SortData;
}

export function getDynListQuerySortFromElement(
  element: ExpressionElement
): Array<SortData> {
  const dynQuerySort = Expressions.getIn(
    Expressions.getElementData(element),
    dynQuerySortPath
  );

  if (!dynQuerySort) {
    return [];
  }

  const mapExprToSortData = matchExpression<SortData | null>({
    Record: mapRecordToSortData,
    _: constant(null),
  });

  // dynQuerySort can be either:
  // - Record (object format)
  // - List<Record> (multiple object formats)
  const evaluatedDynQuerySort = matchExpression<
    SortData | Array<SortData | null> | null
  >({
    List: (list) => list.map(mapExprToSortData),
    Record: mapRecordToSortData,
    _: constant(null),
  })(dynQuerySort);

  if (evaluatedDynQuerySort === null) {
    return [];
  }

  if (Array.isArray(evaluatedDynQuerySort)) {
    return evaluatedDynQuerySort.filter(nonNullish);
  }

  return [evaluatedDynQuerySort];
}

export function getDynListQuerySortExprFromElement(
  element: ExpressionElement
): Expression | null {
  const dynListQuerySortExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQuerySortPath
  );

  return dynListQuerySortExpr || null;
}

export function getCollectionListTemporaryUiState(
  element: ExpressionElement
): string | null {
  return Expressions.getElementDataAt(element, TEMP_STATE_PATH, 'Text', null);
}

const dynBindRepeaterPath = [dynPath, bindPath, repeaterPath];

const getBoundRepeaterFieldPathFromElement = (element: ExpressionElement) => {
  const path = Expressions.getIn(
    Expressions.getElementData(element),
    dynBindRepeaterPath
  );
  if (!path) {
    return emptyArray;
  }
  const evaluatedRepeaterPath = Expressions.evaluate(
    EvalEnv.emptyEvalEnv,
    path
  );

  return Array.isArray(evaluatedRepeaterPath)
    ? evaluatedRepeaterPath
    : emptyArray;
};

export const getBoundRepeaterFieldIdPathFromElement = weakMemo(
  (element: ExpressionElement): string[] => {
    const evaluatedRepeaterPath = getBoundRepeaterFieldPathFromElement(element);
    return evaluatedRepeaterPath.map(
      (bindingPathFragment) => bindingPathFragment.id
    );
  }
);

export const getBoundRepeaterFieldSlugPathFromElement = weakMemo(
  (element: ExpressionElement) => {
    const evaluatedRepeaterPath = getBoundRepeaterFieldPathFromElement(element);
    return createRepeaterSlugPath(evaluatedRepeaterPath);
  }
);

export const getRepeaterFieldSlugPathFromClosestWrapper = weakMemo(
  (element: ExpressionElement) =>
    weakMemo((tree: Expression) => {
      const dynamoWrapper = closestDynamoWrapperElement(tree, element);
      const repeaterSlugPath =
        dynamoWrapper &&
        getBoundRepeaterFieldSlugPathFromElement(dynamoWrapper);
      return repeaterSlugPath;
    })
);

// We need to memoize the repeater creation in order to not break the memoization in NodeDataValidationUtils._memoizedCleanUpNodeData
const createRepeaterSlugPath = weakMemo(
  (
    repeater: Array<{
      [key: string]: any;
    }>
  ): string[] => repeater.map((field) => field.slug || '', [])
);

export function isUnboundDynamoWrapper(element: ExpressionElement): boolean {
  const dynCollectionData = Expressions.getIn(
    Expressions.getElementData(element),
    [dynPath, collectionPath]
  );
  const dynBindRepeaterData = Expressions.getIn(
    Expressions.getElementData(element),
    repeaterIdPath
  );
  return isDynamoWrapper(element) && !dynCollectionData && !dynBindRepeaterData;
}

/**
 * Determines whether a dynamic list is bound to a CMS collection.
 */
export function isCollectionBoundList(element: ExpressionElement): boolean {
  return (
    isDynamoWrapper(element) &&
    getCollectionIdFromElement(element) !== undefined &&
    !isItemRefSetBoundList(element)
  );
}

export function getDynContextFilterValues(
  element: ExpressionElement
): string[] {
  const dynListQueryFilter = getDynListQueryFilterExprFromElement(element);

  if (!dynListQueryFilter) {
    return [];
  }

  return getDynContextFilterValuesFromFilterExpr(dynListQueryFilter);
}

export function getDynContextFilterValuesFromFilterExpr(
  dynListQueryFilterExpr: Expression
): string[] {
  const dynListQueryFieldDistinct = Expressions.getDistinct(
    dynListQueryFilterExpr
  );
  const fieldsExpr = dynListQueryFieldDistinct
    ? Expressions.getIn(dynListQueryFieldDistinct.value, [fieldsPath])
    : Expressions.getIn(dynListQueryFilterExpr, [fieldsPath]);
  const dynListQueryFieldsArray = fieldsExpr
    ? Expressions.getList(fieldsExpr)
    : null;

  if (!dynListQueryFieldsArray) {
    return [];
  }

  return dynListQueryFieldsArray.reduce(
    (acc: string[], filterExpr: Expression) => {
      const filter = Expressions.getRecord(filterExpr);
      const filterValue = filter?.value;
      // Check against Text value
      const filterValueText = filterValue
        ? Expressions.getText(filterValue)
        : null;
      if (filterValueText && matchesDynContextConstant(filterValueText)) {
        acc.push(filterValueText);
        return acc;
      }
      // Check against List[Text] value
      const filterValueList = filterValue
        ? Expressions.getList(filterValue)
        : null;
      if (filterValueList) {
        filterValueList.forEach((listItemExpr) => {
          const textValue = Expressions.getText(listItemExpr) || '';
          if (matchesDynContextConstant(textValue)) {
            acc.push(textValue);
          }
        });
        return acc;
      }
      return acc;
    },
    []
  );
}

function hasDynContextFilter(element: ExpressionElement): boolean {
  return getDynContextFilterValues(element).length > 0;
}

export function isDynamoWrapperWithDynContextFilter(
  element: ExpressionElement
): boolean {
  return isDynamoWrapper(element) && hasDynContextFilter(element);
}

/**
 * Returns DYN_CONTEXT values found in `element` legacy conditional visibility.
 */
export function getLegacyDynContextConditionValues(
  element: ExpressionElement
): string[] {
  const elementConditions = getConditionDataFromElement(element);
  if (!elementConditions) {
    return [];
  }
  const deepestValues: unknown[] = getDeepestValues(elementConditions);

  if (!deepestValues) {
    return [];
  }

  const dynContextConditionValues: string[] = [];
  for (const value of deepestValues) {
    if (typeof value === 'string' && matchesDynContextConstant(value)) {
      dynContextConditionValues.push(value);
    }
  }

  return dynContextConditionValues;
}

/**
 * If the expression is a Dynamo universal binding representing a DYN_CONTEXT
 * condition value, return the binding prop (i.e. "DYN_CONTEXT" or some variant
 * of "DYN_CONTEXT.field-slug"). Returns null otherwise.
 */
export function getDynContextConditionValue({
  from,
  prop,
}: {
  from: Expression;
  prop: string;
}): string | null {
  if (isDynamoVariable(from) && matchesDynContextConstant(prop)) {
    return prop;
  }
  return null;
}

const notFoundButResume = Expressions.resume([]);
const notFoundAndStop = Expressions.stop([]);

/**
 * Returns DYN_CONTEXT values found as universal bindings in expression.
 */
const getUniversalDynContextConditionValues = Expressions.everything<string[]>(
  concat
)(
  matchExpression({
    Element: constant(notFoundAndStop),
    Select: (select) => {
      const prop = getDynContextConditionValue(select);
      return prop ? Expressions.stop([prop]) : notFoundButResume;
    },
    _: constant(notFoundButResume),
  })
);

/**
 * Returns DYN_CONTEXT values found in `element`.
 */
export function getDynContextConditionValues(
  element: ExpressionElement
): string[] {
  return [
    ...getLegacyDynContextConditionValues(element),
    ...getUniversalDynContextConditionValues(
      Expressions.getElementData(element)
    ),
  ];
}

export function isDynConditionedWithDynContextCondition(
  element: ExpressionElement
): boolean {
  return getDynContextConditionValues(element).length > 0;
}

export const isDynamoWrapperWithRandomSort = (
  element: ExpressionElement | null
): boolean => {
  if (!element || !isDynamoWrapper(element)) {
    return false;
  }

  const sortQuery = getDynListQuerySortFromElement(element);

  return sortQuery.some((sort) => {
    return sort.field === '_random';
  });
};

const sortPropertyExpression = (property: string, ascending: boolean) =>
  ERecord({
    field: EText(property),
    direction: EText(ascending ? 'asc' : 'desc'),
  });

export function closestParentDynamoWrapperElement(
  tree: Expression,
  element: ExpressionElement
): ExpressionElement | null {
  const wrapper = closestDynamoWrapperElement(tree, element);
  const parentItem = wrapper ? closestDynamoItemElement(tree, wrapper) : null;
  return parentItem ? closestDynamoWrapperElement(tree, parentItem) : null;
}

export const isInsideRepeaterListItem = weakMemo((element: ExpressionElement) =>
  weakMemo((tree: Expression) => {
    const dynamoItem = closestDynamoItemElement(tree, element);
    const repeaterSlugPath =
      dynamoItem &&
      getRepeaterFieldSlugPathFromClosestWrapper(dynamoItem)(tree);
    return Boolean(repeaterSlugPath && repeaterSlugPath.length > 0);
  })
);
export const isInsideRepeaterWithImageRefSet = weakMemo(
  (element: ExpressionElement) =>
    weakMemo((tree: Expression) => {
      const dynamoItem = closestDynamoItemElement(tree, element);
      const dynamoWrapper =
        dynamoItem && closestDynamoWrapperElement(tree, dynamoItem);
      return Boolean(
        dynamoWrapper &&
        getBoundRepeaterFieldIdPathFromElement(dynamoWrapper).length > 0 &&
        getBoundRepeaterFieldPathFromElement(dynamoWrapper).some(
          ({innerType}) => innerType === 'ImageRef'
        )
      );
    })
);
export const containsItemRefSetBoundList = (element: ExpressionElement) =>
  contains(element, isItemRefSetBoundList);

export const containsNestedDynamoWrapper = (element: ExpressionElement) =>
  contains(element, (el) => isDynamoItem(el) && containsDynamoWrapper(el));

export const containsDynamoWrapper = (element: ExpressionElement): boolean =>
  contains(element, isDynamoWrapper);

export const containsDynConditionedWithDynContextCondition = (
  element: ExpressionElement
): boolean => contains(element, isDynConditionedWithDynContextCondition);

export const isInsideUnboundDynList = isInside(isUnboundDynamoWrapper);

export const isInsideDynList = isInside(isDynamoWrapper);

export const containsDynamoWrapperWithDynContextFilter = (
  element: ExpressionElement
): boolean => contains(element, isDynamoWrapperWithDynContextFilter);

/**
 * Returns true if ExpressionElement contains connected DynamoWrappers,
 * dynamic bindings or conditional visibility.
 */
export const containsConnectedBoundOrConditionedNodes = (
  element: ExpressionElement
) => {
  return contains(
    element,
    (el) =>
      isConnectedDynamoWrapper(el) ||
      isDynamicBoundOrConditioned(el) ||
      hasDynamoUniversalBindings(el)
  );
};

export const containsBoundOrConditionedElements = (
  element: ExpressionElement
) => {
  return contains(
    element,
    (el) => isDynamicBoundOrConditioned(el) || hasDynamoUniversalBindings(el)
  );
};

type InsideDynamoItem = Readonly<{insideDynamoItem: boolean}>;
const INSIDE_DYNAMO_ITEM: InsideDynamoItem = Object.freeze({
  insideDynamoItem: true,
});
const NOT_INSIDE_DYNAMO_ITEM: InsideDynamoItem = Object.freeze({
  insideDynamoItem: false,
});

/**
 * Returns the number of nested dynamo wrappers inside the given expression.
 */
const countNestedDynamoWrappersInExpr: (expr: Expression) => number =
  Expressions.everythingWithContext<InsideDynamoItem>(NOT_INSIDE_DYNAMO_ITEM)(
    add
  )((expression) => (context) => {
    const element = Expressions.getElement(expression);
    let count = 0;
    let nextContext = context;
    if (element) {
      if (context.insideDynamoItem && isDynamoWrapper(element)) {
        count = 1;
      }
      if (isDynamoItem(element)) {
        nextContext = INSIDE_DYNAMO_ITEM;
      }
    }
    return pair(nextContext, count);
  });

/**
 * Returns the number of nested dynamo wrappers inside the page component.
 */
export const countNestedDynamoWrappers = weakMemo(
  (components: Component.ComponentMap): number => {
    const pageComponent = getPageComponentFromComponentMap(components);
    // nested collection lists are not allowed inside components,
    // so we only need to count occurrences inside the page component render
    if (!pageComponent) return 0;
    return countNestedDynamoWrappersInExpr(Component.getRender(pageComponent));
  }
);

export const getDynListLimits = () =>
  getConfigOutsideOfReact<DynListLimitsConfig>(
    'dc-cmsext-ncl-limits',
    defaultDynListLimits
  );

export const getNestedListLimitExceededError = (limit?: number): string => {
  const MAX_NESTED_DYNLISTS_PER_PAGE =
    limit ?? getDynListLimits().MAX_NESTED_DYNLISTS_PER_PAGE;

  return `You cannot have more than ${MAX_NESTED_DYNLISTS_PER_PAGE} nested Collection ${pluralize(
    MAX_NESTED_DYNLISTS_PER_PAGE,
    'List'
  )} on a single page.`;
};

export const limitNestedCollectionListCount =
  (components: Component.ComponentMap, dynListLimits?: DynListLimitsConfig) =>
  (element: ExpressionElement): Result<string, ExpressionElement> => {
    const {MAX_NESTED_DYNLISTS_PER_PAGE} = dynListLimits ?? getDynListLimits();
    if (countNestedDynamoWrappers(components) > MAX_NESTED_DYNLISTS_PER_PAGE) {
      return Err(getNestedListLimitExceededError(MAX_NESTED_DYNLISTS_PER_PAGE));
    }
    return Ok(element);
  };

/**
 * Returns all DynLists inside of element
 */
export const countDynamoWrappers = weakMemo((element: ExpressionElement) =>
  weakMemo((components: Component.ComponentMap): number => {
    return Expressions.everything(add)(
      matchExpression({
        Element: (el) => {
          if (isUserComponentInst(components, el)) {
            const componentRoot = getComponentInstRootElement(components)(el);
            return componentRoot
              ? Expressions.resume(
                  countDynamoWrappers(componentRoot)(components)
                )
              : Expressions.resume(0);
          } else if (isDynamoWrapper(el)) {
            return Expressions.resume(1);
          } else {
            return Expressions.resume(0);
          }
        },
        _: constant(Expressions.resume(0)),
      })
    )(EElement(element));
  })
);

export const limitCollectionListCount =
  (
    components: Component.ComponentMap,
    dynListLimits: DynListLimitsConfig | undefined,
    isNewElement: boolean = true
  ) =>
  (element: ExpressionElement): Result<string, ExpressionElement> => {
    const pageComponent = getPageComponentFromComponentMap(components);
    const pageComponentElement = pageComponent
      ? Expressions.getElement(Component.getRender(pageComponent))
      : undefined;
    const bodyElement = pageComponentElement
      ? findElement(pageComponentElement, isBody)
      : undefined;
    const collectionListsInBodyCount = bodyElement
      ? countDynamoWrappers(bodyElement)(components)
      : 0;
    //If it's s a new element being added to the canvas, we need to count the collection lists in this element plus the ones in the whole body
    //If it's an existing element that's being moved, we just need to count the nodes in the whole body.
    const collectionListsBeingAddedCount = isNewElement
      ? countDynamoWrappers(element)(components)
      : 0;
    const collectionListCountAfterDrop =
      collectionListsBeingAddedCount + collectionListsInBodyCount;

    const {MAX_DYNLISTS_PER_PAGE} = dynListLimits ?? getDynListLimits();
    return collectionListCountAfterDrop > MAX_DYNLISTS_PER_PAGE
      ? Err(
          'You cannot have more than ' +
            MAX_DYNLISTS_PER_PAGE +
            ' Collection Lists on a single page.'
        )
      : Ok(element);
  };

export const usePrimaryLocalePath: ExpressionPath = [
  dynPath,
  queryPath,
  {in: 'Record', at: 'usePrimaryLocale'},
];

export const getUsePrimaryLocale = (element: ExpressionElement): boolean =>
  Expressions.getElementDataAt(element, usePrimaryLocalePath, 'Boolean', false);

export const setUsePrimaryLocale = (
  element: ExpressionElement,
  usePrimaryLocale: boolean
): ExpressionElement => {
  const dataExpr = Expressions.getElementData(element);
  const nextDataExpr = Expressions.sparseSetIn(
    dataExpr,
    usePrimaryLocalePath,
    EBoolean(usePrimaryLocale)
  );
  return Expressions.setElementData(element, nextDataExpr);
};

export function setDynListOffset(
  dynamoWrapper: ExpressionElement,
  offsetValue: number
) {
  const dataExpr = Expressions.getElementData(dynamoWrapper);
  const nextDataExpr = Expressions.sparseSetIn(
    dataExpr,
    dynQueryOffsetPath,
    ENumber(Math.max(offsetValue, MIN_COLLECTION_LIST_OFFSET))
  );
  return Expressions.setElementData(dynamoWrapper, nextDataExpr);
}

export function resetDynListLimitAndOffset(dynamoWrapper: ExpressionElement) {
  let nextElementData = Expressions.deleteInRecord(
    Expressions.getElementData(dynamoWrapper),
    limitItemsPath
  );

  nextElementData = Expressions.deleteInRecord(
    nextElementData,
    dynQueryOffsetPath
  );

  const queryExpr = Expressions.getIn(nextElementData, [dynPath, queryPath]);
  const queryExprRecord = queryExpr ? Expressions.getRecord(queryExpr) : null;

  if (!queryExprRecord || Object.keys(queryExprRecord).length === 0) {
    // clean up query object if it would be left empty
    nextElementData = Expressions.deleteInRecord(nextElementData, [
      dynPath,
      queryPath,
    ]);
  }

  return Expressions.setElementData(dynamoWrapper, nextElementData);
}

export function setDynListLimit(
  dynamoWrapper: ExpressionElement,
  limitValue: number | null,
  isNestedWrapper: boolean = false
) {
  if (limitValue === null) {
    const exprWithNullLimit = Expressions.deleteInRecord(
      Expressions.getElementData(dynamoWrapper),
      limitItemsPath
    );

    return Expressions.setElementData(dynamoWrapper, exprWithNullLimit);
  }
  const isPaginatedList = isPaginatedDynamoWrapperElement(dynamoWrapper);

  const isMultiReferenceOrSetFieldBoundList =
    isItemRefSetBoundList(dynamoWrapper) ||
    isSetFieldDynamoWrapperElement(dynamoWrapper);

  /*
    Collection list limit setting does not have a maximum value
    if the list is connected to a multi-reference or set field, or
    if the list is paginated.
  */
  const noMaximumLimit = isPaginatedList || isMultiReferenceOrSetFieldBoundList;
  const cappedLimitValue =
    capDynamoWrapperLimit(noMaximumLimit)(isNestedWrapper)(limitValue);

  const elemData = Expressions.getElementData(dynamoWrapper);
  const exprWithUpdatedLimit = Expressions.sparseSetIn(
    elemData,
    limitItemsPath,
    ENumber(cappedLimitValue)
  );

  return Expressions.setElementData(dynamoWrapper, exprWithUpdatedLimit);
}

export function toggleDynListQueryLimitAndOffset(
  dynamoWrapper: ExpressionElement
) {
  const dynamoWrapperData = Expressions.getElementData(dynamoWrapper);
  // Either limit or offset are non-null, if limit and offset are enabled
  const limitExpr = Expressions.getIn(dynamoWrapperData, limitItemsPath);
  const offsetExpr = Expressions.getIn(dynamoWrapperData, dynQueryOffsetPath);
  const isLimitAndOffsetEnabled = Boolean(limitExpr || offsetExpr);

  if (isLimitAndOffsetEnabled) {
    return resetDynListLimitAndOffset(dynamoWrapper);
  }

  // When we enable limit and offset, we only set the initial offset value.
  // The presence of an offset value indicates that the "Limit Items" checkbox
  // is checked. If limit value is null, we always use a default limit value
  // determined based on pagination being enabled and if the list is nested.
  return setDynListOffset(dynamoWrapper, MIN_COLLECTION_LIST_OFFSET);
}

/**
 * Returns the repeater's data of the field binded.
 */
export function getBoundRepeaterFieldData(node: ExpressionElement): Array<{
  id: string;
  slug: string;
  type: string;
  innerType?: string;
}> | null {
  const repeaterBinding = getBoundRepeaterFieldPathFromElement(node) as Array<{
    id: string;
    slug: string;
    type: string;
    innerType?: string;
  }>;
  if (repeaterBinding.length === 0) {
    return null;
  }

  // Find the metadata part of the repeater data
  const metaDataPartIndex = repeaterBinding.findIndex((repeaterBind) => {
    const id = repeaterBind.id;
    const slug = repeaterBind.slug;

    return id && slug && id === slug;
  });

  // Otherwise, slice from beginning of the binding list to the metadata item
  return repeaterBinding
    .slice(
      0,
      metaDataPartIndex === -1 ? repeaterBinding.length : metaDataPartIndex
    )
    .map((bindingPathPart) => {
      const id = bindingPathPart.id;
      const slug = bindingPathPart.slug;

      const type = bindingPathPart.type;
      const innerType = bindingPathPart.innerType;

      return innerType
        ? {
            id,
            slug,
            type,
            innerType,
          }
        : {id, slug, type};
    });
}

export type DynamoWrapperCollectionData = {
  id: string;
};
export type DynamoWrapperItemRefSetData = {
  id: string;
  itemRefSet: string;
  contextCollectionId: string;
};

export function createDynamoWrapperDataFromConnection(
  connection: ListConnectionDef
): DynamoWrapperCollectionData | DynamoWrapperItemRefSetData | null {
  switch (connection._kind) {
    case 'LegacyDynamoWrapperConnection/Collection': {
      return {id: connection.id};
    }
    case 'LegacyDynamoWrapperConnection/ItemRefSet': {
      return {
        id: connection.id,
        itemRefSet: connection.itemRefSet,
        contextCollectionId: connection.contextCollectionId,
      };
    }
    default: {
      return null;
    }
  }
}

export function findDynamoWrapperConnection(
  dynamoWrapper: ExpressionElement,
  connections: ListConnectionDef[]
): ListConnectionDef | null {
  if (!isDynamoWrapper(dynamoWrapper) || connections.length === 0) {
    return null;
  }

  const repeaterData = getBoundRepeaterFieldData(dynamoWrapper);
  if (repeaterData && repeaterData.length > 0) {
    return (
      connections.find((connection) => {
        return (
          connection._kind === 'LegacyDynamoWrapperConnection/ImageRefSet' &&
          connection.path.length === repeaterData.length &&
          connection.path.every((fragment, index) => {
            const {id, slug, type, innerType} = repeaterData[index] || {};
            return (
              fragment.id === id &&
              fragment.slug === slug &&
              fragment.type === type &&
              (fragment.innerType || null) === (innerType || null)
            );
          })
        );
      }) || null
    );
  }

  const collectionId = getCollectionIdFromElement(dynamoWrapper);
  const itemRefSetSlug = getCollectionItemRefSetSlugFromElement(dynamoWrapper);
  const contextCollectionId = getContextCollectionIdFromElement(dynamoWrapper);

  if (collectionId && itemRefSetSlug && contextCollectionId) {
    return (
      connections.find((connection) => {
        return (
          connection._kind === 'LegacyDynamoWrapperConnection/ItemRefSet' &&
          connection.id === collectionId &&
          connection.itemRefSet === itemRefSetSlug &&
          connection.contextCollectionId === contextCollectionId
        );
      }) || null
    );
  }

  if (collectionId && !itemRefSetSlug && !contextCollectionId) {
    return (
      connections.find((connection) => {
        return (
          connection._kind === 'LegacyDynamoWrapperConnection/Collection' &&
          connection.id === collectionId
        );
      }) || null
    );
  }

  return null;
}

export function hasDynamoUniversalBindings(element: ExpressionElement) {
  const elementData = Expressions.getElementData(element);
  return getDynamoBindingPaths(elementData).length > 0;
}

const getInitialExpressionArrayForElement = (element: ExpressionElement) => {
  if (isButton(element)) {
    return defaultButtonChildren;
  }

  if (isLink(element) && !isBlockExplicit(element)) {
    return defaultTextLinkChildren;
  }

  if (isHeading(element)) {
    return defaultHeadingChildren;
  }

  if (isParagraph(element)) {
    return defaultParagraphChildren;
  }

  if (isStrong(element)) {
    return defaultStrongChildren;
  }

  if (isEmphasized(element)) {
    return defaultEmChildren;
  }
  if (isSuperscript(element)) {
    return defaultSupChildren;
  }
  if (isSubscript(element)) {
    return defaultSubChildren;
  }

  if (isSpan(element)) {
    return defaultSpanChildren;
  }

  if (isTypographicBlock(element)) {
    return defaultTextDivChildren;
  }

  if (isBlockquote(element)) {
    return defaultBlockquoteChildren;
  }

  if (isRichText(element)) {
    return defaultRichTextChildren;
  }
  return [];
};

export const getInitialChildrenForElement = (
  element: ExpressionElement,
  nextId: string
): Pair<string, Expression[]> => {
  const childrenExpression = getInitialExpressionArrayForElement(element);

  const placeHolderElement = EElement({
    id: 'temporary-holder',
    type: qualify(Plugins.name('Basic'), Component.name('Block')),
    data: ERecord({
      children: EList(childrenExpression),
    }),
  });

  const withNewIDs =
    instantiateExpressionWithUniqueIds(placeHolderElement)(nextId);

  const newTmpElement = getSecond(withNewIDs);
  const newTmpElementExpr = Expressions.getElement(newTmpElement);
  const newChildren = newTmpElementExpr ? getChildren(newTmpElementExpr) : null;

  return pair(
    getFirst(withNewIDs).id,
    newChildren ? newChildren.map(EElement) : []
  );
};

export function addDynQuerySort(
  dynamoWrapper: ExpressionElement,
  property: string,
  ascending: boolean
) {
  const dataExpr = Expressions.getElementData(dynamoWrapper);
  const nextDataExpr = Expressions.sparseUpdateIn(
    dataExpr,
    dynQuerySortPath,
    (sortExpr) => {
      const sortExprList = sortExpr
        ? (Expressions.getList(sortExpr) ?? [])
        : [];

      return EList([
        ...sortExprList,
        sortPropertyExpression(property, ascending),
      ]);
    }
  );
  return Expressions.setElementData(dynamoWrapper, nextDataExpr);
}

export function setDynQuerySort(
  dynamoWrapper: ExpressionElement,
  index: number,
  property: string,
  ascending: boolean
) {
  return updateElementData((el) => {
    return Expressions.sparseSetIn(
      el,
      [...dynQuerySortPath, {in: 'List', index}],
      sortPropertyExpression(property, ascending)
    );
  })(dynamoWrapper);
}

export function removeDynQuerySort(
  dynamoWrapper: ExpressionElement,
  index: number
) {
  const dataExpr = Expressions.getElementData(dynamoWrapper);
  const nextDataExpr = Expressions.sparseUpdateIn(
    dataExpr,
    dynQuerySortPath,
    (sortExpr) => {
      const sortExprList = sortExpr
        ? (Expressions.getList(sortExpr) ?? [])
        : [];
      return EList(deleteAt(sortExprList, index));
    }
  );
  return Expressions.setElementData(dynamoWrapper, nextDataExpr);
}

export function moveDynQuerySort(
  dynamoWrapper: ExpressionElement,
  startIndex: number,
  endIndex: number
) {
  const dataExpr = Expressions.getElementData(dynamoWrapper);
  const nextDataExpr = Expressions.sparseUpdateIn(
    dataExpr,
    dynQuerySortPath,
    (sortExpr) => {
      const sortExprList = sortExpr
        ? (Expressions.getList(sortExpr) ?? [])
        : [];
      return EList(moveElement(sortExprList, startIndex, endIndex));
    }
  );
  return Expressions.setElementData(dynamoWrapper, nextDataExpr);
}

export function addDynQueryParam(
  dynamoWrapper: ExpressionElement,
  fieldPath: string,
  operator: OperatorId,
  value: string | number | null,
  value2: string | number | null,
  value3: string | number | null,
  fieldType: string
): ExpressionElement {
  const {operatorName, value: queryValue} = mapFieldsQuery(
    operator,
    value,
    value2,
    value3,
    fieldType
  );

  return updateElementData((el) => {
    return Expressions.sparseUpdateIn(el, dynQueryFieldsPath, (maybeFields) => {
      const fields = maybeFields
        ? (Expressions.getList(maybeFields) ?? [])
        : [];
      return EList([
        ...fields,
        ERecord({
          fieldPath: EText(fieldPath),
          operatorName: EText(operatorName),
          value:
            typeof queryValue === 'string'
              ? EText(queryValue)
              : ENumber(queryValue),
        }),
      ]);
    });
  })(dynamoWrapper);
}

export function removeDynQueryParam(
  dynamoWrapper: ExpressionElement,
  index: number
): ExpressionElement {
  const withUpdatedFields = updateElementData((el) => {
    return Expressions.sparseUpdateIn(el, dynQueryFieldsPath, (fieldsExpr) => {
      const fieldsExprList = fieldsExpr
        ? (Expressions.getList(fieldsExpr) ?? [])
        : [];
      return EList(deleteAt(fieldsExprList, index));
    });
  })(dynamoWrapper);
  const fieldsExpr = getDynListQueryFieldsExprFromElement(withUpdatedFields);
  const fields = fieldsExpr ? Expressions.getList(fieldsExpr) : [];

  if (fields && fields.length === 0) {
    return updateElementData((el) => {
      const withFieldsRemoved = Expressions.deleteInRecord(
        el,
        dynQueryFieldsPath
      );
      const withFilterForRemoved = Expressions.recursiveDeleteInRecord(
        withFieldsRemoved,
        dynQueryFilterForPath
      );
      return withFilterForRemoved;
    })(withUpdatedFields);
  }

  return withUpdatedFields;
}

export function updateDynQueryParam(
  dynamoWrapper: ExpressionElement,
  fieldPath: string,
  operator: OperatorId,
  value: string | number | null,
  value2: string | number | null,
  value3: string | number | null,
  fieldType: string,
  index: number,
  shouldUpdateValue = true
): ExpressionElement {
  const {operatorName, value: queryValue} = mapFieldsQuery(
    operator,
    value,
    value2,
    value3,
    fieldType
  );
  const recordPath: ExpressionPath = [
    ...dynQueryFieldsPath,
    {in: 'List', index},
  ];

  const newValueExpr =
    typeof queryValue === 'string' ? EText(queryValue) : ENumber(queryValue);
  const valueExpr = shouldUpdateValue
    ? newValueExpr
    : Expressions.getIn(Expressions.getElementData(dynamoWrapper), [
        ...recordPath,
        {in: 'Record', at: 'value'},
      ]) || newValueExpr;

  return updateElementData((el) =>
    Expressions.sparseSetIn(
      el,
      recordPath,
      ERecord({
        fieldPath: EText(fieldPath),
        operatorName: EText(operatorName),
        value: valueExpr,
      })
    )
  )(dynamoWrapper);
}

export function canBindInnerHTML(element: ExpressionElement): boolean {
  // Always allow innerHTML binding for CommerceCheckoutSummaryTextSpacingOnDiv atom
  if (isCommerceCheckoutSummaryTextSpacingOnDiv(element)) {
    return true;
  }

  // Always allow innerHTML binding for rich text nodes, even if they have children inside
  if (isRichText(element)) {
    return true;
  }

  // Typographic nodes (or divs in text:true mode) can have innerHTML replaced dynamically
  if (isTypographic(element)) {
    return true;
  }

  return false;
}

const COLLECTION_LIST_ITEMS_PREVIEW_ENABLED_PATH: ExpressionPath = [
  {in: 'Record', at: 'temp'},
  {in: 'Record', at: 'collectionListItemsPreviewEnabled'},
];

const COLLECTION_LIST_ITEMS_PREVIEW_COUNT_PATH: ExpressionPath = [
  {in: 'Record', at: 'temp'},
  {in: 'Record', at: 'collectionListItemsPreviewCount'},
];

export const getTemporaryCollectionListItemsPreviewEnabledFromWrapper = (
  element: ExpressionElement
): 'GlobalDefault' | 'True' | 'False' => {
  if (!isDynamoWrapper(element)) {
    return 'GlobalDefault';
  }

  const previewExpr = Expressions.getIn(
    Expressions.getElementData(element),
    COLLECTION_LIST_ITEMS_PREVIEW_ENABLED_PATH
  );
  if (!previewExpr) return 'GlobalDefault';
  const previewUnion = Expressions.getUnion(previewExpr);
  if (!previewUnion) return 'GlobalDefault';
  if (previewUnion.tag === 'GlobalDefault') return 'GlobalDefault';
  if (previewUnion.tag === 'True') return 'True';
  if (previewUnion.tag === 'False') return 'False';
  return 'GlobalDefault';
};

export const getTemporaryCollectionListItemsPreviewCountFromWrapper = (
  element: ExpressionElement
): number | undefined => {
  if (!isDynamoWrapper(element)) {
    return undefined;
  }

  return Expressions.getElementDataAt(
    element,
    COLLECTION_LIST_ITEMS_PREVIEW_COUNT_PATH,
    'Number',
    undefined
  );
};

const LINK_BINDING_PATHS: ExpressionPath[] = [
  BIND_PATHS.dyn.href,
  BIND_PATHS.dyn.dataWHref,
  BIND_PATHS.search.href,
  BIND_PATHS.search.dataWHref,
];

export function dropLegacyLinkBindingsIfLinkModeChanged(
  newElement: ExpressionElement,
  oldElement: ExpressionElement
): ExpressionElement {
  const newLinkMode = getLinkElementMode(newElement);
  const oldLinkMode = getLinkElementMode(oldElement);

  if (newLinkMode === oldLinkMode) {
    return newElement;
  }

  const data = Expressions.getElementData(newElement);
  const updatedData = dropLegacyLinkBindings(data);

  if (updatedData === data) {
    return newElement;
  }

  return Expressions.setElementData(newElement, updatedData);
}

export function dropLegacyLinkBindings(data: Expression): Expression {
  return LINK_BINDING_PATHS.reduce(
    (acc, path) => Expressions.deleteInRecord(acc, path),
    data
  );
}
